<script setup lang="ts">
import { useToastStore } from '@/stores/toast.store';
import { calculatePercentage, formatAmount } from '@/utils';
import axios from 'axios';
import moment from 'moment';
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

    const toastStore = useToastStore();
    const BACKEND_URL = inject('BACKEND_URL');
    const route = useRoute();
    const startupInfos = reactive<{ salaires: any[] }>((route.meta as any).item.startup_infos);
    const collectivite = ref<any>((route.meta as any).item.collectivite);
    const importDatas = ref<any[]>((route.meta as any).item.collectivite.users as Array<any>);
    const filteredDatas = ref([] as Array<any>);
    const loading = ref(false);
    const errorMessage = ref('');
    const queryParams = reactive({
        pageNumber: 1
    });
    const pagination = reactive({
        total: 0,
        per_page: 5,
        last_page: 1
    });
    const filtersParams = reactive({
        name: '',
        statut: null,
        salaires: null,
        quotite_cessible_min: null,
        quotite_cessible_max: null,
        age_min: null,
        age_max: null,
        sexe: null,
        fonction: null
    });
    const paginatedImportDatas = ref([] as Array<any>);

    onMounted(() => {
        applyFilters();
        onPaginate(1);
    })

    watch(filtersParams, (oldValues, newValues) => {
        applyFilters();
        onPaginate(1);
    });

    const statsPersonnesActivesCount = computed(() => importDatas.value.filter(i => i.statut == 'Actif').length);
    const statsPersonnesActivesPercent = computed(() => {
        if (importDatas.value.length == 0) { return 0; }
        return (statsPersonnesActivesCount.value * 100) / importDatas.value.length;
    });

    const statsPersonnesRetraitesCount = computed(() => importDatas.value.filter(i => i.statut == 'Retraité').length);
    const statsPersonnesRetraitesPercent = computed(() => {
        if (importDatas.value.length == 0) { return 0; }
        return (statsPersonnesRetraitesCount.value * 100) / importDatas.value.length;
    });

    const statsPersonnesHommesCount = computed(() => importDatas.value.filter(i => i.sexe == 'M').length);
    const statsPersonnesHommesPercent = computed(() => {
        if (importDatas.value.length == 0) { return 0; }
        return (statsPersonnesHommesCount.value * 100) / importDatas.value.length;
    });

    const statsPersonnesFemmesCount = computed(() => importDatas.value.filter(i => i.sexe == 'F').length);
    const statsPersonnesFemmesPercent = computed(() => {
        if (importDatas.value.length == 0) { return 0; }
        return (statsPersonnesFemmesCount.value * 100) / importDatas.value.length;
    });

    const membersPercentage = computed(() => {
        const maxMembers = 50000; // Valeur par défaut pour la barre de progression
        return Math.min(100, calculatePercentage(collectivite.value.users_count, maxMembers));
    });

    function applyFilters() {
        let allDatas = importDatas.value;

        if (filtersParams.name) {
            allDatas = allDatas.filter(a => `${a.nom} ${a.prenom}`.toLowerCase().includes(filtersParams.name!));
        }
        if (filtersParams.statut) {
            allDatas = allDatas.filter(a => a.statut == filtersParams.statut);
        }
        if (filtersParams.sexe) {
            allDatas = allDatas.filter(a => a.sexe == filtersParams.sexe);
        }
        if (filtersParams.quotite_cessible_min && !isNaN(filtersParams.quotite_cessible_min)) {
            const quotite_cessible_min = parseFloat(filtersParams.quotite_cessible_min);
            allDatas = allDatas.filter(a => a.quotite_cessible_montant && !isNaN(a.quotite_cessible_montant) && a.quotite_cessible_montant >= quotite_cessible_min);
        }
        if (filtersParams.quotite_cessible_max && !isNaN(filtersParams.quotite_cessible_max)) {
            const quotite_cessible_max = parseFloat(filtersParams.quotite_cessible_max);
            allDatas = allDatas.filter(a => a.quotite_cessible_montant && !isNaN(a.quotite_cessible_montant) && a.quotite_cessible_montant <= quotite_cessible_max);
        }
        if (filtersParams.age_min) {
            const age_min = moment(filtersParams.age_min);
            if (age_min.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_naissance = moment(a.date_naissance);
                    return date_naissance.isValid() && date_naissance.isSameOrAfter(age_min);
                })
            }
        }
        if (filtersParams.age_max) {
            const age_max = moment(filtersParams.age_max);
            if (age_max.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_naissance = moment(a.date_naissance);
                    return date_naissance.isValid() && date_naissance.isSameOrBefore(age_max);
                })
            }
        }
        if (filtersParams.salaires) {
            const matchSalaire = startupInfos.salaires[filtersParams.salaires];
            if (matchSalaire) {
                allDatas = allDatas.filter(a => !isNaN(a.salaire) && parseFloat(a.salaire) >= parseFloat(matchSalaire.salaire_min) && parseFloat(a.salaire) <= parseFloat(matchSalaire.salaire_max));
            }
        }
        
        filteredDatas.value = allDatas;
    }

    function onPaginate(page: number) {
        queryParams.pageNumber = page;
        pagination.total = filteredDatas.value.length;
        pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
        const start = (page - 1) * pagination.per_page;
        paginatedImportDatas.value =  filteredDatas.value.slice(start, start + pagination.per_page);
    }

</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Collectivité / Membres <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <!-- light2 border-none -->
            <span class="btn shadow-200 white2">Collectivités</span>
            <span class="btn shadow-200 white2">Membres</span>
        </div>


        <!--Collectivité info - Component-->
        <div class="s20 widget flex align-items-center px-1 py-5">
            <div class="s2_mobile h-100p px-7">
                <div class="rounded-100 overflow-hidden item active"><!--active-->
                    <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/assets/logos/logo2.svg'" width="100%" />
                </div>
            </div>
            <div class="s4_mobile h-100p px-2">
                <h4 class="mb-2">{{ collectivite.nom || '' }}</h4>
                <h3>{{ collectivite.rccm || 'N/A' }}</h3>
            </div>
            <div class="s3_mobile h-100p px-2">
                <h4 class="font-normal mb-5">{{ collectivite.users_count || 0 }} / 50000 Membres</h4>
                <span class="tag-state warning stroke">En cours</span>
                <span class="tag-state notif stroke">Actif</span>
            </div>
            <div class="s4_mobile h-100p px-3">
                <h4 class="font-normal">
                    Date de souscription <br class="mb-2"/>
                    <b class="text-2xl">11 Janvier 2028</b>
                </h4>
            </div>
            <div class="s7_mobile h-100p px-5">
                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">Membres {{ collectivite.users_count ? Math.round((collectivite.users_count / 50000) * 100) : 0 }}%</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{ width: membersPercentage + '%' }">
                        {{ collectivite.users_count || 0 }} / 50000
                    </div>
                </div>
                <!--End Barre de progression-->
            </div>
        </div>
        <!--End Collectivité info-->
        <div class="clear-both h-20"></div>

        
        <div class="px-5 py-6 s16 widget"><!--s16 - ajouter-->
            <!-- Filtre - ajouter -->
            <div class="my-5">
                <div class="grid-filter6 mt-4 mb-0">
                    <div class="form-field-floating reactive sm">
                        <input type="text" class="form-input" v-model="filtersParams.name" name="name" id="name" placeholder="">
                        <label for="name">Nom, prénoms</label>
                    </div>
                    <div class="form-field-floating sm">
                        <select class="form-input-select" v-model="filtersParams.statut" name="statut" id="statut">
                            <option :value="null">Tous les statuts</option>
                            <option value="Actif">Actifs</option>
                            <option value="Retraité">Retraités</option>
                        </select>
                        <label for="statut">Statut <b class="color-danger">*</b></label>
                    </div>
                    <div class="form-field-floating sm">
                        <select class="form-input-select" v-model="filtersParams.salaires" name="salaires" id="salaires">
                            <option :value="null">Tous les salaires</option>
                            <option :value="index" v-for="(salaire, index) of startupInfos.salaires" :key="index">{{ formatAmount(salaire.salaire_min) + ' - ' + formatAmount(salaire.salaire_max) }}</option>
                        </select>
                        <label for="salaires">Salaires <b class="color-danger">*</b></label>
                    </div>
                    <div class="form-field-floating reactive sm">
                        <input type="number" class="form-input" v-model="filtersParams.quotite_cessible_min" name="quotite_cessible_min" id="quotite_cessible_min" placeholder="">
                        <label for="quotite_cessible_min">Quotité cessible &gt;=</label>
                    </div>
                    <div class="form-field-floating reactive sm">
                        <input type="number" class="form-input" v-model="filtersParams.quotite_cessible_max" name="quotite_cessible_max" id="quotite_cessible_max" placeholder="">
                        <label for="quotite_cessible_max">Quotité cessible &gt;=</label>
                    </div>
                    <div class="form-field-floating reactive sm">
                        <input type="date" class="form-input" v-model="filtersParams.age_min" name="age_min" id="age_min" placeholder="">
                        <label for="age_min">Age &gt;=</label>
                    </div>
                    <div class="form-field-floating reactive sm">
                        <input type="date" class="form-input" v-model="filtersParams.age_max" name="age_max" id="age_max" placeholder="">
                        <label for="age_max">Age &lt;=</label>
                    </div>
                    <div class="form-field-floating sm">
                        <select class="form-input-select" v-model="filtersParams.sexe" name="sexe" id="sexe">
                            <option :value="null">Tous les sexes</option>
                            <option value="M">Masculin</option>
                            <option value="F">Féminin</option>
                        </select>
                        <label for="sexe">Sexe <b class="color-danger">*</b></label>
                    </div>
                    <div class="form-field-floating sm">
                        <select class="form-input-select" v-model="filtersParams.fonction" name="fonction" id="fonction">
                            <option :value="null">Toutes les fonctions</option>
                        </select>
                        <label for="fonction">Fonction <b class="color-danger">*</b></label>
                    </div>
                </div>
            </div>
            <!---->
            <div class="py-6 s20">
                <table class="table separator skin1 sm selection">
                    <!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded) sm  -->
                    <thead>
                        <tr>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> 
                                Nom
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                Prénoms
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                Salaire brut
                            </th>
                            <th>
                                <img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" />
                                Quotité cessible
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                Contact
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                CpteFonctAg
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                Identifiant Agent
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                FonctionAg
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                CatégAg
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                Statut
                            </th>
                            <th>
                                <img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" />
                                Sexe
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="(dataRow, index) in paginatedImportDatas" :key="'row_' + index">
                            <tr class="text-grey-700 over-effect"><!-- over-effect active-->
                                <td class="font-bold">{{ dataRow.nom }}</td>
                                <td>{{ dataRow.prenom }}</td>
                                <td class="font-bold">{{ formatAmount(dataRow.salaire) }} F CFA</td>
                                <td class="font-bold">{{ formatAmount(dataRow.quotite_cessible_montant) }} <br /> ({{ dataRow.quotite_cessible_pourcent }}%)</td>
                                <td class="text-sm text-grey-500">{{ dataRow.telephone }} <br /> {{ dataRow.email }}</td>
                                <td>{{ dataRow.cpte_fonct_ag }}</td>
                                <td>{{ dataRow.identifiant_ag }}</td>
                                <td>{{ dataRow.fonction_ag }}</td>
                                <td>{{ dataRow.categ_ag }}</td>
                                <td>{{ dataRow.statut }}</td>
                                <td>{{ dataRow.sexe }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>

                <div class="clear-both h-15"></div>

                <div class="s14_mobile px-3">
                    <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                </div>

            </div>
            <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>
        </div>


        <!-- Stat - ajouter -->
        <div class="s4 pl-5 pr-7">
            <!--stat circle big-->
            <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
                <div class="single-chart s6">
                    <svg viewBox="0 0 36 36" class="circular-chart statorange">
                    <path class="circle-bg"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path class="circle"
                        :stroke-dasharray="statsPersonnesActivesPercent + ', 100'"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesActivesPercent }}%</text>
                    </svg>
                </div>
                <div class="s14 px-4">
                    <h3 class="font-medium">{{ statsPersonnesActivesCount }}</h3>
                    <h6 class="font-medium">Personnes actives</h6>
                </div>
            </div>
            <!---->
            <!--stat circle big-->
            <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
                <div class="single-chart s6">
                    <svg viewBox="0 0 36 36" class="circular-chart statorange">
                    <path class="circle-bg"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path class="circle"
                        :stroke-dasharray=" statsPersonnesRetraitesPercent + ', 100'"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesRetraitesPercent }}%</text>
                    </svg>
                </div>
                <div class="s14 px-4">
                    <h3 class="font-medium">{{ statsPersonnesRetraitesCount }}</h3>
                    <h6 class="font-medium">Personnes retraités</h6>
                </div>
            </div>
            <!---->
            <!--stat circle big-->
            <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
                <div class="single-chart s6">
                    <svg viewBox="0 0 36 36" class="circular-chart statorange">
                    <path class="circle-bg"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path class="circle"
                        :stroke-dasharray="statsPersonnesHommesPercent + ', 100'"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesHommesPercent }}%</text>
                    </svg>
                </div>
                <div class="s14 px-4">
                    <h3 class="font-medium">{{ statsPersonnesHommesCount }}</h3>
                    <h6 class="font-medium">Hommes</h6>
                </div>
            </div>
            <!---->
            <!--stat circle big-->
            <div class="w-100 margin-auto mb-5 widget flex-center rounded-15">
                <div class="single-chart s6">
                    <svg viewBox="0 0 36 36" class="circular-chart statorange">
                    <path class="circle-bg"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path class="circle"
                        :stroke-dasharray="statsPersonnesFemmesPercent + ', 100'"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831 
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <text x="18" y="20.35" class="percentage font-bold">{{ statsPersonnesFemmesPercent }}%</text>
                    </svg>
                </div>
                <div class="s14 px-4">
                    <h3 class="font-medium">{{ statsPersonnesFemmesCount }}</h3>
                    <h6 class="font-medium">Femmes</h6>
                </div>
            </div>
            <!---->
        </div>
        <!---->


        <div class="clear-both h-70"></div>
    </section>
</template>